import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AppService } from '../app.service';
import { AuthenticateService } from '../authenticate/authenticate.service';
@Injectable({
  providedIn: 'root'
})
export class AppInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router,
    private appService: AppService,
    private authService: AuthenticateService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (request.url.includes('/auth/refresh-token')) {
      return next.handle(request);
    }
    const userData = JSON.parse(localStorage.getItem('optplanner_user') || 'null');
    // console.log('interceptor')

      if (userData?.token && this.isTokenExpiringSoon(userData)) {


      if (!this.isRefreshing) {
        return this.refreshTokenProactively(userData).pipe(
          switchMap((newToken: string | null) => {
            if (newToken) {
              const authReq = this.addToken(request, newToken);
              return next.handle(authReq).pipe(
                tap(event => {
                  if (event instanceof HttpResponse) {
                    this.appService.showLoader(false);
                  }
                }),
                catchError(error => {
                  if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.handle401Error(authReq, next);
                  }
                  this.appService.showLoader(false);
                  return throwError(() => error);
                })
              );
            } else {
              const authReq = userData?.token ? this.addToken(request, userData.token) : request;
              return next.handle(authReq).pipe(
                tap(event => {
                  if (event instanceof HttpResponse) {
                    this.appService.showLoader(false);
                  }
                }),
                catchError(error => {
                  if (error instanceof HttpErrorResponse && error.status === 401) {
                    return this.handle401Error(authReq, next);
                  }
                  this.appService.showLoader(false);
                  return throwError(() => error);
                })
              );
            }
          })
        );
      } else {
        return this.refreshTokenSubject.pipe(
          filter(token => token !== null),
          take(1),
          switchMap(token => {
            const authReq = this.addToken(request, token!);
            return next.handle(authReq).pipe(
              tap(event => {
                if (event instanceof HttpResponse) {
                  this.appService.showLoader(false);
                }
              }),
              catchError(error => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                  return this.handle401Error(authReq, next);
                }
                this.appService.showLoader(false);
                return throwError(() => error);
              })
            );
          })
        );
      }
    }

    let authReq = request;
    if (userData?.token) {
      authReq = this.addToken(request, userData.token);
    }

    return next.handle(authReq).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.appService.showLoader(false);
        }
      }),
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(authReq, next);
        }

        this.appService.showLoader(false);
        return throwError(() => error);
      })
    );
  }

  // private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
  //   const isTheralinkApi = request.url.includes('theralink.ai');
  //   const isLogoutApi = request.url.includes('/auth/logout');
    
  //   return request.clone({
  //     setHeaders: {
  //       Authorization: (isTheralinkApi || isLogoutApi) ? `Bearer ${token}` : token,
  //       'ngrok-skip-browser-warning': 'true'
  //     }
  //   });
  // }

   private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const isTheralinkApi = request.url.includes('theralink.ai');
    const isLogoutApi = request.url.includes('/auth/logout');
 
    const userData = JSON.parse(localStorage.getItem('optplanner_user') || 'null');
    const headers: any = {
      Authorization: (isTheralinkApi || isLogoutApi) ? `Bearer ${token}` : token,
      'ngrok-skip-browser-warning': 'true'
    };
 
    if (!isTheralinkApi) {
      if (userData?.companyId) {
        headers['x-company-id'] = String(userData.companyId);
      }
    }
 
    return request.clone({
      setHeaders: headers
    });
  }

  private isTokenExpiringSoon(userData: any): boolean {
    if (!userData?.expiresAt) {
      return false;
    }

    const now = Date.now();
    const expiresAt = Number(userData.expiresAt);

    if (expiresAt <= now) {
      // console.warn('Token already expired');
      return true;
    }

    const REFRESH_THRESHOLD = 10 * 1000;

    const isExpiringSoon = (expiresAt - now) <= REFRESH_THRESHOLD;

    // console.log({
    //   now: new Date(now).toISOString(),
    //   expiresAt: new Date(expiresAt).toISOString(),
    //   diffMs: expiresAt - now,
    //   isExpiringSoon
    // });

    return isExpiringSoon;
  }

  private refreshTokenProactively(userData: any): Observable<string | null> {
    const refreshToken = userData.refreshToken;
    const companyId = userData.companyId;

    if (!refreshToken) {
      return throwError(() => 'No refresh token available');
    }

    if (userData.refreshExpiresAt && Date.now() >= userData.refreshExpiresAt) {
      this.logout();
      return throwError(() => 'Refresh token expired');
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    // console.log('Proactively refreshing token...');
    return this.authService
      .refreshLoginFastTokenAPI(refreshToken, companyId)
      .pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;

          const updatedUserData = JSON.parse(localStorage.getItem('optplanner_user') || 'null');
          if (updatedUserData) {
            updatedUserData.token = response.access_token;
            updatedUserData.refreshToken = response.refresh_token;
            updatedUserData.expiresAt = response.access_expires_at * 1000;   // convert to ms
            updatedUserData.refreshExpiresAt = response.refresh_expires_at * 1000; // convert to ms

            localStorage.setItem('optplanner_user', JSON.stringify(updatedUserData));
            // console.log('Token refreshed successfully! New expiry:', new Date(updatedUserData.expiresAt).toLocaleTimeString());
          }

          this.refreshTokenSubject.next(response.access_token);
          return of(response.access_token);
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          // console.warn('Proactive token refresh failed:', err);
          return of(null);
        })
      );
  }


  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    const userData = JSON.parse(localStorage.getItem('optplanner_user') || 'null');

    if (!userData || (userData.refreshExpiresAt && Date.now() >= userData.refreshExpiresAt)) {
      this.logout();
      return throwError(() => 'Refresh token expired');
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = userData.refreshToken;
      const companyId = userData.companyId;

      return this.authService
        .refreshLoginFastTokenAPI(refreshToken, companyId)
        .pipe(
          switchMap((response: any) => {
            this.isRefreshing = false;

            const updatedUserData = JSON.parse(localStorage.getItem('optplanner_user') || 'null');
            if (updatedUserData) {
              updatedUserData.token = response.data.token;
              updatedUserData.expiresAt = response.data.expiresAt;
              updatedUserData.refreshExpiresAt = response.data.refreshExpiresAt;
              updatedUserData.refreshToken = response.data.refreshToken || updatedUserData.refreshToken;

              localStorage.setItem('optplanner_user', JSON.stringify(updatedUserData));
            }

            this.refreshTokenSubject.next(response.data.token);

            return next.handle(this.addToken(request, response.data.token));
          }),
          catchError(err => {
            this.isRefreshing = false;
            this.logout();
            return throwError(() => err);
          })
        );
    }

    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token =>
        next.handle(this.addToken(request, token!))
      )
    );
  }

  private logout() {
    localStorage.removeItem('optplanner_user');
    // this.appService.clearAuthorities();
    this.router.navigate(['/login'], { skipLocationChange: true });
  }
}
